import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import AHDate from "_shared/dates/date";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { canNameTargetsOf, useGetPermissionTargetsByIdsQuery } from "permissions/_api/permission-targets-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters, { PermissionOrderField } from "permissions/_api/queries/ordering/permission-order-params";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { useGetPermissionsQuery } from "permissions/_api/permissions-api-service";
import { type PersonRecordRow, groupRecordGrants } from "permissions/_views/people/person-record-grants-model";
import useRecordNameSearch, { type RecordSearchScope, canSearchRecordsOf } from "permissions/_views/people/use-record-name-search";
import { type NameState, nameStates } from "permissions/_views/targets/permission-target-state";
import Permission from "permissions/permission";
import { useMemo } from "react";

/** One page of a per-record list. Small enough to name every record on it in one lookup per page. */
export const RECORD_PAGE_SIZE = 25;

/**
 * The records a list is narrowed to: the generic `targetIds` filter, which the server matches against
 * the id property of the list's one resource type, so it serves every per-record kind (#6174).
 */
function idFilter(ids: readonly string[]): Partial<PermissionFilterParameters> {
  return { targetIds: ids.filter((id) => Guid.isValid(id)).map((id) => new Guid(id)) };
}

/** Before any grant: the floor that makes `minDisabledDate` mean "removed at any time". */
const BEFORE_ANY_REMOVAL = new AHDate("2000-01-01");

/**
 * Which grants a per-record list holds: the live ones, or under the person page's Removed filter the
 * removed ones. Never both in one paged list. The server pages grants, not records, so a removed
 * grant on a later page than the live grant that replaced it would draw as a second, switched-off row
 * for a record the person still holds, offering to grant again what they have (#6275 review).
 */
export type RecordListMode = "live" | "removed";

/**
 * `GET /permissions?view=Flat&granteeUserIds=X&resourceTypes=<kind>`, one page at a time: the live
 * grants newest first, or the removed ones latest removal first. A name search narrows it to the ids
 * the name search found.
 */
export function recordQuery(
  userId: Guid,
  kind: PermissionKind,
  mode: RecordListMode,
  pageIndex: number,
  ids: readonly string[] | undefined,
  pageSize = RECORD_PAGE_SIZE,
): PermissionQuery {
  const removed = mode === "removed";
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex, pageSize }),
    orderParameters: new PermissionOrderParameters({
      orderBy: removed ? PermissionOrderField.DisabledDate : PermissionOrderField.CreatedDate,
      orderDir: OrderDirection.Descending,
    }),
    filterParameters: new PermissionFilterParameters({
      granteeUserIds: [userId],
      resourceTypes: [kind.resourceType],
      ...(removed ? { minDisabledDate: BEFORE_ANY_REMOVAL } : { showDisabled: false }),
      ...(ids ? idFilter(ids) : {}),
    }),
  });
}

export type PersonRecordGrants = Readonly<{
  rows: readonly PersonRecordRow[];
  /** Every grant the list holds, on every page. */
  total: number;
  pageCount: number;
  isLoading: boolean;
  /** The list could not be read; said in the list, with Retry. */
  error?: string;
  retry: () => void;
  /** Whether the kind's records can be found by name. */
  canSearch: boolean;
  isSearching: boolean;
  searchError?: string;
  /** What each record on the page is called, or why it cannot be named. */
  targets: ReadonlyMap<string, NameState>;
  retryTargets: () => void;
}>;

const NO_ROWS: readonly PersonRecordRow[] = [];

export default function usePersonRecordGrants(
  userId: Guid,
  kind: PermissionKind,
  pageIndex: number,
  text: string,
  mode: RecordListMode = "live",
  scope: RecordSearchScope = "organization",
  targetId?: string,
): PersonRecordGrants {
  const canSearch = canSearchRecordsOf(kind.resourceType, scope);
  // A list narrowed to one record asks for that record's grants by id, and no name search runs.
  const searching = !targetId && canSearch && text.trim().length > 0;
  const search = useRecordNameSearch(searching ? kind.resourceType : undefined, text, scope);

  // A search that found nothing has nothing to ask the Permissions API about: an empty id list is no
  // filter at all there, and would answer every grant.
  // A search that failed lists nothing either, and says why.
  const nothingFound = searching && ((search.ids !== undefined && search.ids.length === 0) || search.error !== undefined);
  const waitingForSearch = searching && search.ids === undefined && search.error === undefined;

  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const query = useMemo(() => {
    if (nothingFound || waitingForSearch) return undefined;
    if (targetId) return recordQuery(userId, kind, mode, pageIndex, [targetId]);
    return recordQuery(userId, kind, mode, pageIndex, searching ? search.ids : undefined);
  }, [nothingFound, waitingForSearch, targetId, userId, kind, mode, pageIndex, searching, search.ids]);
  const list = useGetPermissionsQuery(query ? { query } : skipToken, { refetchOnFocus: true });

  const rows = useMemo(
    () => (query && list.data ? groupRecordGrants(kind, Permission.fromJSONArray(list.data.data)) : NO_ROWS),
    [query, list.data, kind],
  );
  const targetIds = useMemo(() => [...new Set(rows.map((row) => row.targetId).filter(Boolean))], [rows]);

  const named = canNameTargetsOf(kind.resourceType);
  const lookup = useGetPermissionTargetsByIdsQuery(
    named && targetIds.length > 0 ? { resourceType: kind.resourceType, ids: targetIds } : skipToken,
  );
  const lookupFailed = !named || lookup.isError;
  const targets = useMemo(
    () => nameStates(targetIds, { data: lookup.currentData, isError: lookupFailed }),
    [targetIds, lookup.currentData, lookupFailed],
  );

  const total = query ? (list.data?.headers.totalElements ?? 0) : 0;
  return {
    rows,
    total,
    pageCount: query ? Math.max(list.data?.headers.totalPages ?? 1, 1) : 1,
    isLoading: waitingForSearch || search.isSearching || (query !== undefined && list.isLoading),
    error: list.error ? (getErrorMessageFromUnknown(list.error) ?? "The grants could not be loaded.") : undefined,
    retry: () => {
      if (query) void list.refetch();
    },
    canSearch,
    isSearching: search.isSearching,
    searchError: search.error,
    targets,
    retryTargets: () => {
      if (named && targetIds.length > 0) void lookup.refetch();
    },
  };
}
